import { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import {
  faqItems,
  steps,
  services,
  voices,
  noticeItems,
  shopDetail,
} from "@/mocks/shop";

/* ===== GSAP Scroll Trigger Hook ===== */
function useFadeIn(
  selector: string,
  options?: { y?: number; stagger?: number; duration?: number },
) {
  const ref = useRef<HTMLDivElement>(null);
  const triggered = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered.current) {
          triggered.current = true;
          const targets = el.querySelectorAll(selector);
          if (targets.length === 0) {
            gsap.fromTo(
              el,
              { opacity: 0, y: options?.y ?? 40 },
              { opacity: 1, y: 0, duration: options?.duration ?? 0.8, ease: "power2.out" },
            );
          } else {
            gsap.fromTo(
              targets,
              { opacity: 0, y: options?.y ?? 40 },
              {
                opacity: 1,
                y: 0,
                duration: options?.duration ?? 0.7,
                stagger: options?.stagger ?? 0.12,
                ease: "power2.out",
              },
            );
          }
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}

/* ===== Count Up Hook ===== */
function useCountUp(target: number, suffix: string) {
  const ref = useRef<HTMLSpanElement>(null);
  const triggered = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered.current) {
          triggered.current = true;
          const obj = { val: 0 };
          gsap.to(obj, {
            val: target,
            duration: 2,
            ease: "power2.out",
            onUpdate: () => {
              if (el) el.textContent = Math.floor(obj.val).toLocaleString() + suffix;
            },
          });
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, suffix]);

  return ref;
}

/* ===== Section Label ===== */
function SectionLabel({ children }: { children: string }) {
  return (
    <span className="inline-block text-xs font-bold text-amber-600 tracking-[0.2em] uppercase mb-3">
      {children}
    </span>
  );
}

export default function ShopLP() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceRef = useFadeIn(".service-card", { y: 30, stagger: 0.12, duration: 0.6 });
  const stepRef = useFadeIn(".step-node", { y: 40, stagger: 0.2, duration: 0.7 });
  const voiceRef = useFadeIn(".voice-card", { y: 30, stagger: 0.12, duration: 0.6 });
  const faqRef = useFadeIn(".faq-row", { y: 20, stagger: 0.08, duration: 0.5 });
  const noticeRef = useFadeIn(".notice-row", { y: 20, stagger: 0.08, duration: 0.5 });
  const shopRef = useFadeIn(".shop-animate", { y: 40, duration: 0.8 });

  const toggleFaq = (index: number) => {
    const next = openFaq === index ? null : index;
    setOpenFaq(next);
    const answerEl = document.querySelector(`.faq-answer-${index}`);
    if (answerEl) {
      if (next === index) {
        gsap.fromTo(
          answerEl,
          { height: 0, opacity: 0 },
          { height: "auto", opacity: 1, duration: 0.3, ease: "power2.out" },
        );
      } else {
        gsap.to(answerEl, { height: 0, opacity: 0, duration: 0.2, ease: "power2.in" });
      }
    }
  };

  return (
    <div className="w-full bg-white">
      {/* ===== HERO ===== */}
      <section className="relative w-full min-h-[560px] md:min-h-[640px] flex items-center overflow-hidden bg-[#0b1120]">
        {/* 背景画像 */}
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20automotive%20repair%20workshop%20interior%20wide%20angle%20shot%2C%20clean%20modern%20garage%20with%20car%20lifts%20and%20tool%20cabinets%2C%20mechanic%20working%20on%20sedan%20under%20bright%20warm%20lighting%2C%20organized%20workspace%20with%20tires%20and%20parts%20shelves%2C%20warm%20amber%20and%20white%20industrial%20lighting%20atmosphere%2C%20cinematic%20photography%20with%20shallow%20depth%20of%20field&width=1400&height=700&seq=10&orientation=landscape"
          alt="アイチモータース 工場内観"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />

        {/* 背景オーバーレイ */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1120]/90 via-[#0b1120]/50 to-[#0b1120]/80" />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-5 md:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* 左：テキスト */}
            <div>
              <div className="inline-flex items-center gap-2 bg-white/8 border border-white/10 rounded-full px-4 py-2 mb-6">
                <i className="ri-map-pin-line text-amber-500 text-base" />
                <span className="text-white/80 text-sm font-medium">愛知県知多市</span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-[1.2] mb-5 tracking-tight">
                地域の皆様の
                <br />
                <span className="text-amber-500">カーライフを支える</span>
                <br />
                アイチモータース
              </h1>

              <p className="text-sm md:text-base text-white/55 leading-relaxed max-w-md">
                車検・整備・板金塗装・LED取付まで、お車のことなら何でもお任せください。
                知多市で35年、地域の皆様に信頼いただいている自動車整備専門店です。
              </p>
            </div>

            {/* 右：店舗情報カード */}
            <div className="hidden lg:block">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
                <p className="text-white font-bold text-sm mb-6">店舗情報</p>

                <div className="space-y-2 text-sm text-white/50">
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-amber-500/60" />
                    <span>営業時間：{shopDetail.businessHours}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-calendar-close-line text-amber-500/60" />
                    <span>定休日：{shopDetail.holiday}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-map-pin-line text-amber-500/60" />
                    <span>{shopDetail.address}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 下部グラデーション */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
      </section>

      {/* ===== PHONE CTA BAR ===== */}
      <section className="relative z-10 -mt-10 mx-4 md:mx-auto max-w-4xl">
        <div className="bg-amber-600 rounded-2xl shadow-xl shadow-amber-600/20 px-6 py-6 md:px-10 md:py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5">
            <div className="text-center md:text-left">
              <p className="text-white/80 text-sm font-medium mb-1">お電話一本でご予約・ご相談OK</p>
              <p className="text-white/60 text-xs">車検・整備・板金・LED取付 何でもお気軽に</p>
            </div>
            <a
              href={`tel:${shopDetail.phone}`}
              className="inline-flex items-center gap-3 bg-white text-amber-700 hover:bg-amber-50 px-7 py-3.5 rounded-xl text-lg font-bold transition-all whitespace-nowrap hover:-translate-y-0.5 hover:shadow-lg"
            >
              <i className="ri-phone-line text-xl" />
              {shopDetail.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="mb-12 md:mb-16 text-center">
            <SectionLabel>SERVICES</SectionLabel>
            <h2 className="text-2xl md:text-4xl font-bold text-[#0b1120] leading-tight mb-3">
              お車のことならお任せください
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-lg mx-auto leading-relaxed">
              車検から日常のメンテナンス、板金塗装まで、幅広く対応いたします。
            </p>
          </div>

          <div ref={serviceRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {services.map((s, i) => (
              <div
                key={i}
                className="service-card group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-amber-500/30 transition-all hover:shadow-lg hover:shadow-amber-500/5"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-600/10 flex items-center justify-center mb-5 group-hover:bg-amber-600/15 transition-colors">
                  <i className={`${s.icon} text-amber-600 text-xl`} />
                </div>
                <h3 className="text-base md:text-lg font-bold text-[#0b1120] mb-2">{s.name}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>

          {/* 電話CTA */}
          <div className="mt-12 text-center">
            <a
              href={`tel:${shopDetail.phone}`}
              className="inline-flex items-center gap-2 text-amber-600 font-bold text-sm hover:text-amber-700 transition-colors"
            >
              <i className="ri-phone-line" />
              サービスの詳細はお電話でお気軽にご相談ください
            </a>
          </div>
        </div>
      </section>

      {/* ===== FLOW ===== */}
      <section className="py-20 md:py-28 bg-[#fafafa]">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="mb-12 md:mb-16 text-center">
            <SectionLabel>FLOW</SectionLabel>
            <h2 className="text-2xl md:text-4xl font-bold text-[#0b1120] leading-tight mb-3">
              ご利用の流れ
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-lg mx-auto leading-relaxed">
              まずはお電話でご予約。あとはお車をご持参ください。
            </p>
          </div>

          <div ref={stepRef} className="relative">
            {/* 接続線（PCのみ） */}
            <div className="hidden md:block absolute top-6 left-[16%] right-[16%] h-[2px] bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
              {steps.map((s, i) => (
                <div key={i} className="step-node relative text-center">
                  <div className="w-12 h-12 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold mx-auto mb-5 shadow-lg shadow-amber-600/20">
                    {String(s.step).padStart(2, "0")}
                  </div>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <i className={`${s.icon} text-amber-600`} />
                    <h3 className="text-base md:text-lg font-bold text-[#0b1120]">{s.title}</h3>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed max-w-xs mx-auto">
                    {s.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* 電話CTA */}
          <div className="mt-12 flex justify-center">
            <a
              href={`tel:${shopDetail.phone}`}
              className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-xl text-base font-bold transition-all whitespace-nowrap hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-600/20"
            >
              <i className="ri-phone-line text-xl" />
              {shopDetail.phoneDisplay} でお電話予約
            </a>
          </div>
        </div>
      </section>

      {/* ===== SHOP INFO ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="mb-12 md:mb-16 text-center">
            <SectionLabel>SHOP INFO</SectionLabel>
            <h2 className="text-2xl md:text-4xl font-bold text-[#0b1120] leading-tight mb-3">
              店舗情報・アクセス
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-lg mx-auto leading-relaxed">
              愛知県知多市八幡。お気軽にお立ち寄りください。
            </p>
          </div>

          <div ref={shopRef} className="shop-animate bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* 左：地図 */}
              <div className="relative h-64 md:h-80 lg:h-auto bg-gray-100">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3267.0!2d136.85!3d34.95!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDU3JzAwLjAiTiAxMzZzwrs1MScwMC4wIkU!5e0!3m2!1sja!2sjp!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="アイチモータース 地図"
                  className="absolute inset-0 w-full h-full"
                />
              </div>

              {/* 右：情報 */}
              <div className="p-6 md:p-8 lg:p-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 mb-7">
                  {[
                    { icon: "ri-map-pin-line", label: "住所", value: shopDetail.address },
                    { icon: "ri-phone-line", label: "電話番号", value: shopDetail.phoneDisplay, isTel: true },
                    { icon: "ri-time-line", label: "営業時間", value: shopDetail.businessHours },
                    { icon: "ri-calendar-close-line", label: "定休日", value: shopDetail.holiday },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-amber-600/10 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-amber-600 text-sm`} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">{item.label}</p>
                        {item.isTel ? (
                          <a
                            href={`tel:${shopDetail.phone}`}
                            className="text-sm text-[#0b1120] font-bold hover:text-amber-600 transition-colors"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-sm text-[#0b1120] font-medium">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gray-50 rounded-xl p-5 mb-6">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {shopDetail.description}
                  </p>
                </div>

                <a
                  href={`tel:${shopDetail.phone}`}
                  className="w-full inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-600/20"
                >
                  <i className="ri-phone-line" />
                  電話でお問い合わせ・予約する
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="mb-12 md:mb-16 text-center">
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="text-2xl md:text-4xl font-bold text-[#0b1120] leading-tight mb-3">
              よくある質問
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-lg mx-auto leading-relaxed">
              ご不明点があれば、お電話でもお気軽にご相談ください。
            </p>
          </div>

          <div ref={faqRef} className="space-y-3">
            {faqItems.map((faq, i) => (
              <div key={i} className="faq-row border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full flex items-start justify-between p-5 text-left hover:bg-gray-50 transition-colors gap-4"
                >
                  <span className="text-sm md:text-base font-bold text-[#0b1120] leading-relaxed">{faq.question}</span>
                  <i
                    className={`ri-${openFaq === i ? "subtract" : "add"}-line text-amber-600 text-lg flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-amber-600/10 mt-0.5`}
                  />
                </button>
                <div className={`faq-answer-${i} overflow-hidden ${openFaq === i ? "" : "h-0 opacity-0"}`}>
                  <div className="px-5 pb-5">
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-gray-500 mb-4">その他のご質問はお電話でお気軽に</p>
            <a
              href={`tel:${shopDetail.phone}`}
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-xl text-sm font-bold transition-all hover:-translate-y-0.5"
            >
              <i className="ri-phone-line" />
              {shopDetail.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* ===== NOTICE ===== */}
      <section className="py-20 md:py-28 bg-[#fafafa]">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="mb-12 md:mb-16 text-center">
            <SectionLabel>IMPORTANT</SectionLabel>
            <h2 className="text-2xl md:text-4xl font-bold text-[#0b1120] leading-tight mb-3">
              ご来店前の確認事項
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-lg mx-auto leading-relaxed">
              スムーズなご対応のため、以下をご確認ください。
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm">
            <div className="flex items-center gap-2.5 mb-6">
              <i className="ri-shield-check-line text-amber-600 text-xl" />
              <h3 className="text-base md:text-lg font-bold text-[#0b1120]">ご利用について</h3>
            </div>
            <div ref={noticeRef} className="space-y-4">
              {noticeItems.map((item, i) => (
                <div key={i} className="notice-row flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-amber-600/10 text-amber-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm text-gray-700 leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="relative py-24 md:py-32 bg-[#0b1120] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-[-30%] right-[-10%] w-[400px] h-[400px] rounded-full bg-amber-600/8 blur-[100px]" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[300px] h-[300px] rounded-full bg-amber-600/5 blur-[80px]" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 tracking-tight">
            お気軽に
            <br className="sm:hidden" />
            お電話ください
          </h2>
          <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-lg mx-auto mb-10">
            車検、整備、板金塗装、LED取付など、お車のことでお困りの際は
            アイチモータースまでお気軽にご相談ください。
          </p>

          <a
            href={`tel:${shopDetail.phone}`}
            className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-700 text-white px-10 py-5 rounded-2xl text-xl md:text-2xl font-bold transition-all whitespace-nowrap hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-600/20 mb-6"
          >
            <i className="ri-phone-line text-2xl md:text-3xl" />
            {shopDetail.phoneDisplay}
          </a>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm text-white/40">
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-amber-500/60" />
              <span>{shopDetail.businessHours}</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/20" />
            <div className="flex items-center gap-2">
              <i className="ri-calendar-close-line text-amber-500/60" />
              <span>定休日：{shopDetail.holiday}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}